import { formatAmount } from '@core/i18n';

export const SKYX_WIN_SHARE_PREFIX = '@skyx-win:';

export interface SkyxWinShareData {
  player: string;
  playerId?: string;
  bet: number;
  win: number;
  cashout: number;
  round: number;
  /** Round number for provably-fair lookup (from leaderboard roundNo). */
  roundId?: string;
  currency: string;
  /** Server CDN avatar URL captured at share time (optional). */
  avatarUrl?: string;
  /** Optional user caption shown below the win card in chat. */
  caption?: string;
}

export function formatSkyxWinShareMessage(data: SkyxWinShareData): string {
  const payload: SkyxWinShareData = { ...data };
  const trimmedCaption = payload.caption?.trim();
  if (trimmedCaption) {
    payload.caption = trimmedCaption;
  } else {
    delete payload.caption;
  }
  if (!payload.avatarUrl?.trim()) {
    delete payload.avatarUrl;
  } else {
    payload.avatarUrl = payload.avatarUrl.trim();
  }
  return `${SKYX_WIN_SHARE_PREFIX}${JSON.stringify(payload)}`;
}

function parseNumericValue(raw: string): number {
  const cleaned = raw.replace(/[^\d.,-]/g, '').replace(/,/g, '');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseLegacyPipeMessage(text: string): SkyxWinShareData | null {
  const parts = text.split(' | ').map(part => part.trim());
  if (parts.length < 5) return null;

  const data: SkyxWinShareData = {
    player: parts[0],
    bet: 0,
    win: 0,
    cashout: 0,
    round: 0,
    currency: '',
  };

  for (const part of parts.slice(1)) {
    const match = part.match(/^(.+?):\s*(.+)$/);
    if (!match) continue;

    const label = match[1].trim().toLowerCase();
    const value = match[2].trim();

    if (label.startsWith('bet') || label.startsWith('apuesta')) {
      const currencyMatch = label.match(/(?:bet|apuesta)\s+(.+)/i);
      if (currencyMatch?.[1]) data.currency = currencyMatch[1].trim();
      data.bet = parseNumericValue(value);
      continue;
    }

    if (label.startsWith('win') || label.startsWith('ganancia')) {
      const currencyMatch = label.match(/(?:win|ganancia)\s+(.+)/i);
      if (currencyMatch?.[1]) data.currency = currencyMatch[1].trim();
      data.win = parseNumericValue(value);
      continue;
    }

    if (label.startsWith('result') || label.startsWith('resultado')) {
      data.cashout = parseNumericValue(value);
      continue;
    }

    if (label.includes('round') || label.includes('ronda')) {
      data.round = parseNumericValue(value);
    }
  }

  const hasStats =
    data.bet > 0 || data.win > 0 || data.cashout > 0 || data.round > 0;
  return hasStats ? data : null;
}

export function parseSkyxWinShareMessage(
  text: string
): SkyxWinShareData | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith(SKYX_WIN_SHARE_PREFIX)) {
    try {
      const parsed = JSON.parse(
        trimmed.slice(SKYX_WIN_SHARE_PREFIX.length)
      ) as Partial<SkyxWinShareData>;
      if (
        typeof parsed.player !== 'string' ||
        typeof parsed.currency !== 'string'
      ) {
        return null;
      }
      return {
        player: parsed.player,
        playerId:
          typeof parsed.playerId === 'string' ? parsed.playerId : undefined,
        bet: Number(parsed.bet) || 0,
        win: Number(parsed.win) || 0,
        cashout: Number(parsed.cashout) || 0,
        round: Number(parsed.round) || 0,
        roundId:
          typeof parsed.roundId === 'string' && parsed.roundId.trim()
            ? parsed.roundId.trim()
            : undefined,
        currency: parsed.currency,
        caption:
          typeof parsed.caption === 'string' && parsed.caption.trim()
            ? parsed.caption.trim()
            : undefined,
        avatarUrl:
          typeof parsed.avatarUrl === 'string' &&
          /^https?:\/\//i.test(parsed.avatarUrl.trim())
            ? parsed.avatarUrl.trim()
            : undefined,
      };
    } catch {
      return null;
    }
  }

  return parseLegacyPipeMessage(trimmed);
}

export function formatWinShareNumber(value: number | undefined | null): string {
  return formatAmount(value);
}
